/**
 * /admin/billing — Capexara internal revenue + consumption overview
 */
import { useEffect, useState } from 'react';
import { BarChart3, IndianRupee, DollarSign, Users, TrendingUp, Activity, RefreshCw, Webhook, CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminBillingApi } from '../../services/billing';
import { formatMoney } from '../../lib/geo';

const STATUS_META = {
  green:   { color: '#10B981', Icon: CheckCircle2, label: 'Healthy' },
  amber:   { color: '#F59E0B', Icon: AlertTriangle, label: 'Degraded' },
  red:     { color: '#EF4444', Icon: XCircle,       label: 'Broken' },
  unknown: { color: '#6B7280', Icon: HelpCircle,    label: 'Untested' },
};

const AdminBilling = () => {
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [health, setHealth] = useState(null);
  const [probing, setProbing] = useState(false);

  const refreshHealth = async () => {
    try {
      const { data } = await adminBillingApi.webhookHealth();
      setHealth(data);
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, u, i, h] = await Promise.all([
          adminBillingApi.summary(),
          adminBillingApi.users({ limit: 100 }),
          adminBillingApi.invoices({ limit: 50 }),
          adminBillingApi.webhookHealth().catch(() => ({ data: null })),
        ]);
        setSummary(s.data); setUsers(u.data.items); setInvoices(i.data.items);
        if (h?.data) setHealth(h.data);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const runProbe = async () => {
    setProbing(true);
    try {
      await adminBillingApi.runWebhookProbe();
      await refreshHealth();
      toast.success('Webhook probe complete');
    } catch (e) {
      toast.error('Probe failed — check backend logs');
    } finally {
      setProbing(false);
    }
  };

  const revInr = summary?.revenue?.find(r => r._id === 'INR');
  const revUsd = summary?.revenue?.find(r => r._id === 'USD');

  return (
    <div className="space-y-6" data-testid="admin-billing">
      <div>
        <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>REVENUE & CONSUMPTION</div>
        <h1 className="font-heading text-3xl font-semibold text-nexus-text-primary mt-1">Billing control plane</h1>
        <p className="text-[12px] text-nexus-text-tertiary mt-1">Capexara AI (OPC) Private Limited · Credit-based PAYG</p>
      </div>

      {/* Webhook health badge — surfaces broken Razorpay webhooks BEFORE
          customers complain. A daily probe POSTs a synthetic event to
          our own /api/billing/webhook with the real HMAC signature; the
          handler short-circuits probe events (no credits granted) and
          this card displays the outcome. Red = ≥2 consecutive failures,
          Amber = last failed OR stale (>36h), Green = healthy. */}
      {(() => {
        const summary = health?.summary || { status: 'unknown', headline: 'Loading…' };
        const meta = STATUS_META[summary.status] || STATUS_META.unknown;
        const Icon = meta.Icon;
        const last = health?.recent?.[0];
        return (
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{
              background: `${meta.color}0F`,
              border: `1px solid ${meta.color}55`,
            }}
            data-testid="webhook-health-card"
          >
            <div
              className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: `${meta.color}22`, color: meta.color }}
            >
              <Icon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: meta.color }}>
                  Razorpay Webhook · {meta.label}
                </div>
                {!health?.configured && (
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded"
                    style={{ background: 'rgba(245,158,11,0.18)', color: '#F59E0B' }}>
                    PUBLIC_BASE_URL not set
                  </span>
                )}
              </div>
              <div className="text-[12.5px] font-semibold text-nexus-text-primary">{summary.headline}</div>
              <div className="text-[10.5px] text-nexus-text-tertiary mt-1 font-mono flex items-center gap-2 flex-wrap">
                {last && (
                  <span>Last: {new Date(last.timestamp).toLocaleString()} · HTTP {last.http_code ?? '—'} · {last.latency_ms ?? '?'}ms</span>
                )}
                {!last && <span>No probes recorded yet.</span>}
                {health?.webhook_url && (
                  <span className="opacity-70 truncate">→ {health.webhook_url}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={runProbe}
              disabled={probing}
              className="flex-shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55`, color: meta.color }}
              data-testid="webhook-health-probe-btn"
            >
              <RefreshCw size={12} className={probing ? 'animate-spin' : ''} />
              {probing ? 'Probing…' : 'Run probe'}
            </button>
          </div>
        );
      })()}

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          { label: 'Revenue (INR)', val: revInr ? formatMoney(revInr.total, 'INR') : '₹0.00', sub: `${revInr?.count || 0} invoices`, icon: IndianRupee, color: '#F26B1F' },
          { label: 'Revenue (USD)', val: revUsd ? formatMoney(revUsd.total, 'USD') : '$0.00', sub: `${revUsd?.count || 0} invoices`, icon: DollarSign, color: '#6BA6F5' },
          { label: 'Credits sold', val: ((revInr?.credits || 0) + (revUsd?.credits || 0)).toLocaleString(), sub: 'lifetime', icon: TrendingUp, color: '#6EE7B7' },
          { label: 'Users with wallet', val: users.length, sub: 'active', icon: Users, color: '#A8B0C0' },
        ].map((k) => (
          <div key={k.label} className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${k.color}22`, color: k.color }}>
                <k.icon size={14} />
              </div>
            </div>
            <div className="mt-3 text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>{k.label}</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-nexus-text-primary">{k.val}</div>
            <div className="text-[11px] text-nexus-text-tertiary mt-0.5">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Consumption by model */}
      <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
        <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>CONSUMPTION BY MODEL (ALL USERS)</div>
        <div className="space-y-2">
          {(summary?.consumption_by_model || []).map((m) => (
            <div key={m._id} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: 'var(--hairline)' }}>
              <span className="text-[12px] text-nexus-text-secondary">{m._id || 'unknown'}</span>
              <div className="flex items-center gap-4">
                <span className="text-[11px] text-nexus-text-tertiary font-mono">{m.calls.toLocaleString()} calls</span>
                <span className="text-[13px] font-semibold text-nexus-text-primary">{m.credits.toFixed(1)} cr</span>
              </div>
            </div>
          ))}
          {(!summary?.consumption_by_model || summary.consumption_by_model.length === 0) && (
            <div className="text-[12px] text-nexus-text-tertiary py-4 text-center">No consumption yet.</div>
          )}
        </div>
      </div>

      {/* Users */}
      <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
        <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>USERS ({users.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead style={{ background: 'var(--layer-0)', color: '#718096' }}>
              <tr>
                <th className="text-left px-3 py-2 font-semibold">User</th>
                <th className="text-left px-3 py-2 font-semibold">Currency</th>
                <th className="text-right px-3 py-2 font-semibold">Balance</th>
                <th className="text-right px-3 py-2 font-semibold">Lifetime</th>
                <th className="text-left px-3 py-2 font-semibold">Bracket</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b" style={{ borderColor: 'var(--hairline)' }}>
                  <td className="px-3 py-2">
                    <div className="text-nexus-text-primary">{u.name || u.email}</div>
                    <div className="text-[10px] text-nexus-text-tertiary">{u.email}</div>
                  </td>
                  <td className="px-3 py-2 text-nexus-text-secondary">{u.wallet?.currency || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-nexus-text-primary">{Number(u.wallet?.credit_balance || 0).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right font-mono text-nexus-text-secondary">{(u.wallet?.lifetime_credits_purchased || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-nexus-text-tertiary text-[11px]">
                    {(u.wallet?.lifetime_credits_purchased || 0) >= 5001 ? '5001+' :
                     (u.wallet?.lifetime_credits_purchased || 0) >= 1001 ? '1001-5000' : '0-1000'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent invoices */}
      <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
        <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>RECENT INVOICES</div>
        <div className="space-y-1">
          {invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: 'var(--hairline)' }}>
              <div>
                <div className="text-[13px] font-semibold text-nexus-text-primary">{inv.invoice_number}</div>
                <div className="text-[11px] text-nexus-text-tertiary">
                  {inv.credits_purchased} credits · {inv.created_at?.slice(0, 10)}
                </div>
              </div>
              <div className="text-[13px] font-semibold text-nexus-text-primary">{formatMoney(inv.total, inv.currency)}</div>
            </div>
          ))}
          {invoices.length === 0 && (
            <div className="text-[12px] text-nexus-text-tertiary py-4 text-center">No invoices yet.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBilling;
